<template>
  <v-container
    fluid
    class="pa-0 mt-6"
  >
    <!-- ───────── SKELETON STATE ───────── -->
    <template v-if="loading">
      <!-- Skeleton Row 1 -->
      <v-row
        dense
        class="mb-3"
      >
        <v-col
          v-for="n in 3"
          :key="'sk1-' + n"
          cols="12"
          md="4"
        >
          <v-card
            class="stat-card pa-5"
            flat
            outlined
          >
            <div class="d-flex justify-space-between align-start mb-4">
              <v-skeleton-loader
                type="avatar"
                width="44"
                height="44"
                class="skel-icon"
              />
              <v-skeleton-loader
                type="chip"
                width="72"
                height="22"
                class="skel-chip"
              />
            </div>
            <v-skeleton-loader
              type="heading"
              width="60"
              class="mb-2"
            />
            <v-skeleton-loader
              type="text"
              width="140"
            />
          </v-card>
        </v-col>
      </v-row>

      <!-- Skeleton Row 2 -->
      <v-row dense>
        <v-col
          v-for="n in 3"
          :key="'sk2-' + n"
          cols="12"
          md="4"
        >
          <v-card
            class="stat-card pa-5"
            flat
            outlined
          >
            <div class="d-flex align-center">
              <v-skeleton-loader
                type="avatar"
                width="44"
                height="44"
                class="skel-icon mr-4"
              />
              <div>
                <v-skeleton-loader
                  type="heading"
                  width="48"
                  class="mb-2"
                />
                <v-skeleton-loader
                  type="text"
                  width="80"
                />
              </div>
            </div>
          </v-card>
        </v-col>
      </v-row>
    </template>

    <!-- ───────── DATA STATE ───────── -->
    <template v-else>
      <!-- Row 1: Routes, Vehicles Allocated, Active Vehicles -->
      <v-row
        dense
        class="mb-3"
      >
        <v-col
          cols="12"
          md="4"
        >
          <v-card
            class="stat-card pa-5"
            flat
            outlined
          >
            <div class="d-flex justify-space-between align-start mb-4">
              <div class="icon-wrap icon-purple">
                <v-icon
                  color="#6C63FF"
                  size="22"
                >
                  mdi-call-split
                </v-icon>
              </div>
              <span class="badge-chip badge-global">GLOBAL</span>
            </div>
            <div class="stat-number">
              {{ stats?.no_of_routes || 0 }}
            </div>
            <div class="stat-label">
              Number of Routes
            </div>
          </v-card>
        </v-col>

        <v-col
          cols="12"
          md="4"
        >
          <v-card
            class="stat-card pa-5"
            flat
            outlined
          >
            <div class="d-flex justify-space-between align-start mb-4">
              <div class="icon-wrap icon-blue">
                <v-icon
                  color="#2B7FFF"
                  size="22"
                >
                  mdi-truck-outline
                </v-icon>
              </div>
              <span class="badge-chip badge-allocated">ALLOCATED</span>
            </div>
            <div class="stat-number">
              {{ stats?.no_of_vehicles_on_routes || 0 }}
            </div>
            <div class="stat-label">
              Vehicles Allocated to Routes
            </div>
          </v-card>
        </v-col>

        <v-col
          cols="12"
          md="4"
        >
          <v-card
            class="stat-card pa-5"
            flat
            outlined
          >
            <div class="d-flex justify-space-between align-start mb-4">
              <div class="icon-wrap icon-salmon">
                <v-icon
                  color="#E07B6A"
                  size="22"
                >
                  mdi-car-outline
                </v-icon>
              </div>
              <span class="badge-chip badge-live">LIVE</span>
            </div>
            <div class="stat-number">
              {{ stats?.active_vehicles || 0 }}
            </div>
            <div class="stat-label">
              Active Vehicles
            </div>
          </v-card>
        </v-col>
      </v-row>

      <!-- Row 2: On Time, Delayed, Critical -->
      <v-row dense>
        <v-col
          cols="12"
          md="4"
        >
          <v-card
            class="stat-card stat-card--inline pa-5"
            flat
            outlined
          >
            <div class="d-flex align-center">
              <div class="icon-wrap icon-slate mr-4">
                <v-icon
                  color="#5B6A8A"
                  size="20"
                >
                  mdi-clock-outline
                </v-icon>
              </div>
              <div>
                <div class="stat-number stat-number--sm">
                  {{ stats?.on_time || 0 }}
                </div>
                <div class="stat-label">
                  On Time
                </div>
              </div>
            </div>
          </v-card>
        </v-col>

        <v-col
          cols="12"
          md="4"
        >
          <v-card
            class="stat-card stat-card--inline pa-5"
            flat
            outlined
          >
            <div class="d-flex align-center">
              <div class="icon-wrap icon-amber mr-4">
                <v-icon
                  color="#E07B1A"
                  size="20"
                >
                  mdi-alert-outline
                </v-icon>
              </div>
              <div>
                <div class="stat-number stat-number--sm stat-number--amber">
                  {{ stats?.delayed || 0 }}
                </div>
                <div class="stat-label">
                  Delayed
                </div>
              </div>
            </div>
          </v-card>
        </v-col>

        <v-col
          cols="12"
          md="4"
        >
          <v-card
            class="stat-card stat-card--inline pa-5"
            flat
            outlined
          >
            <div class="d-flex align-center">
              <div class="icon-wrap icon-red mr-4">
                <v-icon
                  color="#C0392B"
                  size="20"
                >
                  mdi-asterisk
                </v-icon>
              </div>
              <div>
                <div class="stat-number stat-number--sm stat-number--red">
                  {{ stats?.critical || 0 }}
                </div>
                <div class="stat-label">
                  Critical
                </div>
              </div>
            </div>
          </v-card>
        </v-col>
      </v-row>
    </template>
  </v-container>
</template>

<script>
import { mapGetters } from 'vuex/dist/vuex.common.js';

export default {
  name: 'ArrivalDashboardCards',

  props: {
    // Pass true while your API call is in-flight, false once data is ready
    loading: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    // You can add computed properties here if needed for formatting stats
    ...mapGetters({
        // loading: "arrivalDashboard/getLoading",
        stats: "arrivalDashboard/getAggregattedData",
    })

  },
}
</script>

<style scoped>
/* ── Card base ── */
.stat-card {
  border-radius: 14px !important;
  border: 1.5px solid #ECECEC !important;
  background: #FFFFFF !important;
  transition: box-shadow 0.2s ease;
}

.stat-card:hover {
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.07) !important;
}

/* ── Skeleton tweaks ──
   Force the avatar bone to render as a rounded square, not a circle */
.skel-icon >>> .v-skeleton-loader__avatar {
  border-radius: 10px !important;
  width: 44px !important;
  height: 44px !important;
}

.skel-chip >>> .v-skeleton-loader__chip {
  border-radius: 20px !important;
  height: 22px !important;
}

/* Remove default top-margin Vuetify adds to skeleton bones */
.v-skeleton-loader >>> .v-skeleton-loader__bone {
  margin: 0 !important;
}

/* ── Icon wrappers ── */
.icon-wrap {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.icon-purple { background-color: #EEEEFF; }
.icon-blue   { background-color: #E6F0FF; }
.icon-salmon { background-color: #FAE8E5; }
.icon-slate  { background-color: #E9ECF2; }
.icon-amber  { background-color: #FEF3E2; }
.icon-red    { background-color: #FDECEA; }

/* ── Badge chips ── */
.badge-chip {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.06em;
  padding: 3px 9px;
  border-radius: 20px;
  line-height: 1.6;
}

.badge-global    { background: #F0EFFF; color: #6C63FF; }
.badge-allocated { background: #E6F0FF; color: #2B7FFF; }
.badge-live      { background: #FDECEA; color: #C0392B; }

/* ── Numbers ── */
.stat-number {
  font-size: 34px;
  font-weight: 700;
  color: #1A1A2E;
  line-height: 1.1;
  margin-bottom: 4px;
}

.stat-number--sm    { font-size: 28px; }
.stat-number--amber { color: #E07B1A; }
.stat-number--red   { color: #C0392B; }

/* ── Labels ── */
.stat-label {
  font-size: 13px;
  color: #9096A6;
  font-weight: 400;
}
</style>